import { t } from '../i18n.js';
import { CATALOG_GROUPS, HUB_PATH, groupVisibleCatalogItems, groupForPath } from './catalog-items.js';
import { brandLockup } from './brand-lockup.js';

/**
 * Persistent desktop navigation, rendered as the first column of the shell
 * grid at and above `lg`. Hidden below `lg`, where the bottom tab bar and the
 * hub page carry the same structure at a different density.
 *
 * Deliberately **in normal flow**: no `position: fixed`, no `z-index`. The
 * z-index scale is closed at six levels (`docs/responsive-conventions.md`) and
 * the apparently free z-30 is already taken by two FABs, so a fixed sidebar
 * would have to share z-40 with the bottom nav and paint under every modal,
 * sheet and toast. An in-flow column removes the problem instead of solving it.
 *
 * Must never be mounted inside `<main id="page-outlet">` — a transform ancestor
 * over the outlet breaks `position: fixed` page children (`router.js:97-99`).
 *
 * @param {{ role?: string|null, currentPath?: string }} [props]
 * @returns {{ el: HTMLElement, refresh: (path?: string) => void, destroy: () => void }}
 */
export function sidebar({ role, currentPath } = {}) {
  const root = document.createElement('nav');
  root.dataset.role = 'sidebar-nav';
  root.setAttribute('aria-label', t('nav.primary_aria'));
  root.className = [
    'hidden lg:flex flex-col',
    'bg-surface-light dark:bg-surface-card-dark',
    'border-r border-border dark:border-border-dark',
    'overflow-hidden',
  ].join(' ');

  const head = document.createElement('div');
  // `sidebar-head` pins the same 64 px as `.app-header` and carries the same
  // brand edge, so the top chrome reads as one band instead of two surfaces
  // whose bottom edges miss each other by 3 px.
  head.className = 'sidebar-head flex items-center px-4';
  head.append(brandLockup({ emblemClasses: 'h-9 w-9', wordClasses: 'font-bold text-xl tracking-tight' }));
  root.append(head);

  const scroll = document.createElement('div');
  scroll.className = 'flex-1 overflow-y-auto px-2.5 py-3 flex flex-col gap-3.5';
  root.append(scroll);

  /** @type {Array<{ path: string, group: string, el: HTMLAnchorElement }>} */
  const rows = [];
  /** @type {Array<{ key: string, el: HTMLElement }>} */
  const groupLabels = [];

  // A surface never lists a route that only re-renders that same surface. On
  // the hub page this rule drops the hub's own row; here it drops the hub
  // entirely, because everything the hub would show is already in this
  // sidebar. The route stays registered — deep links and a phone-width resize
  // both still land on it — and the bottom bar still projects it, where it is
  // the one door to the eleven screens four tabs cannot hold.
  const groups = groupVisibleCatalogItems(role)
    .map((g) => ({ ...g, items: g.items.filter((i) => i.path !== HUB_PATH) }))
    .filter((g) => g.items.length > 0);

  for (const group of groups) {
    const section = document.createElement('div');
    section.className = 'flex flex-col gap-0.5';
    section.dataset.groupKey = group.key;

    const label = document.createElement('p');
    label.dataset.role = 'nav-group-label';
    label.dataset.groupKey = group.key;
    label.className = 'section-label px-2.5 pb-1 transition-colors';
    const def = CATALOG_GROUPS.find((g) => g.key === group.key);
    label.textContent = t(def ? def.labelKey : group.labelKey);
    section.append(label);
    groupLabels.push({ key: group.key, el: label });

    for (const item of group.items) {
      const a = document.createElement('a');
      a.href = '#' + item.path;
      a.dataset.path = item.path;
      a.dataset.role = 'sidebar-item';
      a.className = [
        'flex items-center gap-2.5 min-h-[40px] px-2.5 rounded-xl',
        'text-sm font-medium transition-colors',
        'hover:bg-primary-50 dark:hover:bg-white/5',
        'focus:outline-none focus:ring-2 focus:ring-primary',
      ].join(' ');

      a.append(item.iconFn());

      const lbl = document.createElement('span');
      lbl.className = 'flex-1 min-w-0 truncate';
      lbl.textContent = t(item.titleKey);
      a.append(lbl);

      section.append(a);
      rows.push({ path: item.path, group: group.key, el: a });
    }

    scroll.append(section);
  }

  // Active state. Resolved through groupForPath() so a sub-route such as
  // /recipes/:id lights both its row's group heading and — when the row itself
  // is the prefix owner — the row. Exact-path equality alone leaves 17 of 21
  // routes with nothing highlighted, which is the state this replaces.
  const ACTIVE_ROW = ['bg-primary-50', 'dark:bg-primary-900/40', 'text-primary-700', 'dark:text-primary-400'];
  const ACTIVE_LABEL = ['text-primary-600', 'dark:text-primary-500'];

  function refresh(path) {
    const current = path != null ? path : pathFromHash();
    const activeGroup = groupForPath(current);

    for (const row of rows) {
      const isCurrent = row.path === current
        || (row.path !== '/' && current.startsWith(row.path + '/'));
      if (isCurrent) {
        row.el.setAttribute('aria-current', 'page');
        row.el.classList.add(...ACTIVE_ROW);
      } else {
        row.el.removeAttribute('aria-current');
        row.el.classList.remove(...ACTIVE_ROW);
      }
    }

    for (const { key, el } of groupLabels) {
      if (key === activeGroup) {
        el.dataset.groupActive = 'true';
        el.classList.add(...ACTIVE_LABEL);
      } else {
        delete el.dataset.groupActive;
        el.classList.remove(...ACTIVE_LABEL);
      }
    }
  }

  function pathFromHash() {
    const h = typeof window !== 'undefined' ? window.location.hash : '';
    if (!h) return '/';
    const stripped = h.startsWith('#') ? h.slice(1) : h;
    return stripped || '/';
  }

  refresh(currentPath);

  const onHashChange = () => refresh();
  if (typeof window !== 'undefined') window.addEventListener('hashchange', onHashChange);

  function destroy() {
    if (typeof window !== 'undefined') window.removeEventListener('hashchange', onHashChange);
  }

  return { el: root, refresh, destroy };
}
